import React, { useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';

import QuestionStep from "./QuestionStep";
import MenuOption from "./MenuOption";
import MessageOption from "./MessageOption";
import JumpToStep from "./Steps/JumpToStep";
import MenuOptionStep from "./Steps/MenuOptionStep";
import ResolveChatStep from "./Steps/ResolveChatStep";
import { CHATBOT_OPTIONS_TYPE } from '../../../constants/AppConstants';
import ChatbotOptionModel from '../ChatbotOptionModel';
import NoActiveMia from "./Steps/NoActiveMia";
import ApiIcon from "../../icons/ApiIcon";
import ApiOption from "./ApiOption";
import ResolveChatIcon from "../../icons/ResolveChatIcon";
import WhatsappFlowOption from "./WhatsappFlowOption";
import IconMC from '../../../presentation/components/IconMC';

const CUSTOMER_ID_VARIABLE = ['customer_id', 'customer_id'];

const withCustomerIdVariable = (fields = []) => {
  if (fields.some((field) => field?.[1] === CUSTOMER_ID_VARIABLE[1])) return fields;

  return [CUSTOMER_ID_VARIABLE, ...fields];
};

const OptionTypeCard = ({
  iconName,
  icon,
  title,
  description,
  selected = false,
  disabled = false,
  onClick
}) => (
  <button
    type="button"
    className={`tw-flex tw-w-full tw-items-start tw-gap-3 tw-rounded-xl tw-border tw-p-4 tw-text-left tw-transition-colors ${selected ? 'tw-border-blue-400 tw-bg-blue-50' : 'tw-border-gray-200 tw-bg-white hover:tw-border-blue-300'} ${disabled ? 'tw-cursor-not-allowed tw-opacity-50' : 'tw-cursor-pointer'}`}
    onClick={onClick}
    disabled={disabled}
  >
    <span className="tw-mt-0.5 tw-flex tw-flex-shrink-0 tw-text-gray-700">
      {icon || <IconMC name={iconName} size="xl" />}
    </span>
    <span className="tw-flex tw-min-w-0 tw-flex-col">
      <span className="tw-text-sm tw-font-semibold tw-text-gray-900">{title}</span>
      <span className="tw-text-xs tw-text-gray-500">{description}</span>
    </span>
  </button>
);

const OptionBodyComponent = ({
  chatbot,
  chatbotOption,
  setChatbotOption,
  handleOptionClick,
  displayOptionTypes,
  setDisplayOptionTypes,
  errors,
  setErrors,
  handleActiveMiaOptionClick,
  handleCancelOption,
  isIntermediateNode = false
}) => {
  const { t } = useTranslation();

  const [customerColumns, setCustomerColumns] = useState([]);

  useEffect(() => {
    loadCustomerColumns();
  }, []);

  const loadCustomerColumns = () => {
    ChatbotOptionModel.getCustomerColumns().then((res) => {
      setCustomerColumns(withCustomerIdVariable(res.fields));
    });
  };

  const isOptionSelected = (value) => chatbotOption.option_type === value;

  return (
    <div className="container-fluid-no-padding">
      {displayOptionTypes && (
        <div className="tw-flex tw-flex-col tw-gap-4">
          <h2 className="tw-mb-0 tw-text-lg tw-font-semibold tw-text-gray-900">
            {t('chatbot.config.modal.select_step_type')}
          </h2>

          <OptionTypeCard
            iconName="message-square-outline"
            title={t('chatbot.config.options.message')}
            description={t('chatbot.config.options.message_description')}
            selected={isOptionSelected(CHATBOT_OPTIONS_TYPE.MESSAGE)}
            onClick={() => handleOptionClick(CHATBOT_OPTIONS_TYPE.MESSAGE)}
          />

          <OptionTypeCard
            iconName="menu-fill"
            title={t('chatbot.config.options.menu')}
            description={t('chatbot.config.options.menu_description')}
            selected={isOptionSelected(CHATBOT_OPTIONS_TYPE.MENU)}
            onClick={() => handleOptionClick(CHATBOT_OPTIONS_TYPE.MENU)}
          />

          <OptionTypeCard
            iconName="question-mark-circle-outline"
            title={t('chatbot.config.options.question')}
            description={t('chatbot.config.options.question_description')}
            selected={isOptionSelected(CHATBOT_OPTIONS_TYPE.QUESTION)}
            onClick={() => handleOptionClick(CHATBOT_OPTIONS_TYPE.QUESTION)}
          />

          <OptionTypeCard
            iconName="bot"
            title={(
              <>
                {t('chatbot.config.options.active_mia')}
                {isIntermediateNode && (
                  <small className="tw-ml-2 tw-text-gray-500">
                    {t('chatbot.config.options.not_available_in_intermediate_steps')}
                  </small>
                )}
              </>
            )}
            description={t('chatbot.config.options.active_mia_description')}
            selected={isOptionSelected(CHATBOT_OPTIONS_TYPE.ACTIVE_MIA)}
            disabled={isIntermediateNode}
            onClick={() => handleActiveMiaOptionClick(CHATBOT_OPTIONS_TYPE.ACTIVE_MIA)}
          />

          <OptionTypeCard
            iconName="link-2-outline"
            title={(
              <>
                {t('chatbot.config.options.go_to')}
                {isIntermediateNode && (
                  <small className="tw-ml-2 tw-text-gray-500">
                    {t('chatbot.config.options.not_available_in_intermediate_steps')}
                  </small>
                )}
              </>
            )}
            description={t('chatbot.config.options.go_to_description')}
            selected={isOptionSelected(CHATBOT_OPTIONS_TYPE.JUMP_TO)}
            disabled={isIntermediateNode}
            onClick={() => handleOptionClick(CHATBOT_OPTIONS_TYPE.JUMP_TO)}
          />

          <OptionTypeCard
            iconName="message_check"
            title={(
              <>
                {t('chatbot.config.options.resolve_chat')}
                {isIntermediateNode && (
                  <small className="tw-ml-2 tw-text-gray-500">
                    {t('chatbot.config.options.not_available_in_intermediate_steps')}
                  </small>
                )}
              </>
            )}
            description={t('chatbot.config.options.resolve_chat_description')}
            selected={isOptionSelected(CHATBOT_OPTIONS_TYPE.RESOLVE_CHAT)}
            disabled={isIntermediateNode}
            onClick={() => handleActiveMiaOptionClick(CHATBOT_OPTIONS_TYPE.RESOLVE_CHAT)}
          />

          <OptionTypeCard
            icon={<ApiIcon iconName="check-circle-outline" />}
            title={t('chatbot.config.options.api')}
            description={t('chatbot.config.options.api_description')}
            selected={isOptionSelected(CHATBOT_OPTIONS_TYPE.API)}
            onClick={() => handleOptionClick(CHATBOT_OPTIONS_TYPE.API)}
          />

          {chatbot?.whatsapp_enabled && (
            <OptionTypeCard
              iconName="whatsapp-outline"
              title={t('chatbot.config.options.whatsapp_flow')}
              description={t('chatbot.config.options.whatsapp_flow_description')}
              selected={isOptionSelected(CHATBOT_OPTIONS_TYPE.WHATSAPP_FLOW)}
              onClick={() => handleOptionClick(CHATBOT_OPTIONS_TYPE.WHATSAPP_FLOW)}
            />
          )}

          <div>
            <span className="funnel-input-error">{errors.option_type}</span>
          </div>
        </div>
      )}

      {!displayOptionTypes && (
        <>
          {!chatbotOption.fake_option && (
            <>
              {chatbotOption.option_type === CHATBOT_OPTIONS_TYPE.QUESTION && (
                <QuestionStep
                  chatbotOption={chatbotOption}
                  setChatbotOption={setChatbotOption}
                  handleChangeOptionType={() => setDisplayOptionTypes(true)}
                  errors={errors}
                  customerColumns={customerColumns}
                  chatbot={chatbot}
                />
              )}

              {chatbotOption.option_type === CHATBOT_OPTIONS_TYPE.MENU && (
                <MenuOption
                  chatbotOption={chatbotOption}
                  setChatbotOption={setChatbotOption}
                  handleChangeOptionType={() => setDisplayOptionTypes(true)}
                  errors={errors}
                  customerColumns={customerColumns}
                  chatbot={chatbot}
                />
              )}

              {chatbotOption.option_type === CHATBOT_OPTIONS_TYPE.MESSAGE && (
                <MessageOption
                  chatbot={chatbot}
                  chatbotOption={chatbotOption}
                  setChatbotOption={setChatbotOption}
                  errors={errors}
                  setErrors={setErrors}
                  customerColumns={customerColumns}
                />
              )}

              {chatbotOption.option_type === CHATBOT_OPTIONS_TYPE.API && (
                <ApiOption
                  chatbot={chatbot}
                  chatbotOption={chatbotOption}
                  setChatbotOption={setChatbotOption}
                  errors={errors}
                  setErrors={setErrors}
                  customerColumns={customerColumns}
                />
              )}

              {chatbotOption.option_type === CHATBOT_OPTIONS_TYPE.WHATSAPP_FLOW && (
                <WhatsappFlowOption
                  chatbot={chatbot}
                  chatbotOption={chatbotOption}
                  setChatbotOption={setChatbotOption}
                  errors={errors}
                  setErrors={setErrors}
                  customerColumns={customerColumns}
                />
              )}

              {chatbotOption.option_type === CHATBOT_OPTIONS_TYPE.ACTIVE_MIA && (
                <NoActiveMia
                  handleCancelOption={handleCancelOption}
                />
              )}

              {chatbotOption.option_type === CHATBOT_OPTIONS_TYPE.JUMP_TO && (
                <JumpToStep
                  chatbot={chatbot}
                  chatbotOption={chatbotOption}
                  setChatbotOption={setChatbotOption}
                  errors={errors}
                  setErrors={setErrors}
                  customerColumns={customerColumns}
                />
              )}
            </>
          )}

          {chatbotOption.fake_option && (
            <MenuOptionStep
              chatbot={chatbot}
              chatbotOption={chatbotOption}
              setChatbotOption={setChatbotOption}
              customerColumns={customerColumns}
              errors={errors}
            />
          )}
        </>
      )}
    </div>
  );
};

export default OptionBodyComponent;
